import React, { useState } from "react";
import { Star, Quote, Mail, ExternalLink, CheckCircle, Info } from "lucide-react";
import { childcareData } from "../../data/mock";
import Modal from "../ui/modal";

const Testimonials = () => {
  const [showReferralDetails, setShowReferralDetails] = useState(false);
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`h-5 w-5 ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const handleReferralEmail = () => {
    const subject = "Refer a Friend to Heathbourne House Childcare";
    const body = `Hi,

I wanted to share details about an amazing childcare setting I've found - Heathbourne House Childcare.

They're located in a beautiful Georgian house in Bushey Heath with 2 acres of grounds, farm animals, and exceptional care. What makes them special:

• Professional curriculum with unique activities (football, French, yoga, animals)
• Experienced NVQ certified staff (Liza & Jasmin)
• Government funding available (up to 30 hours free per week)
• Beautiful historic setting with extensive grounds
• Tested settler regime to help children transition smoothly

Plus, they offer a £100 referral bonus when your referred friend completes their first month!

Contact details:
📞 Jasmin: +44 759 254 009
📞 Liza: +44 787 055 681  
📧 heathbournehousechildminder@gmail.com
📍 WD23 1PA, Bushey Heath, UK

I think this could be perfect for your family!

Best regards`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleReferralContact = () => {
    const subject = "Referral Programme Enquiry";
    const body = `Hello,

I'm interested in learning more about your £100 referral programme. Could you please provide more details about:

- How the referral process works
- When the £100 payment is made
- Any terms and conditions

Thank you!

Best regards`;

    const mailtoLink = `mailto:${childcareData.referral.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleShowDetails = () => {
    setShowReferralDetails(true);
  };

  return (
    <section className="py-20 bg-white" data-section="testimonials" id="testimonials">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            What 
            <span className="text-blue-500"> Parents Say</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Don't just take our word for it. Here's what our wonderful families have to say about their experience at Heathbourne House.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {childcareData.testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="group relative bg-blue-50 p-8 rounded-2xl border border-blue-100 hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              {/* Quote Icon */}
              <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Quote className="h-16 w-16 text-blue-400" />
              </div>
              
              {/* Stars */}
              <div className="flex items-center space-x-1 mb-4">
                {renderStars(testimonial.rating)}
              </div>

              {/* Testimonial Text */}
              <p className="text-slate-700 mb-6 leading-relaxed italic">
                "{testimonial.text}"
              </p>

              {/* Author Info */}
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-lg">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">{testimonial.name}</h4>
                  <p className="text-sm text-slate-500">
                    {testimonial.child === "Parent" ? "Parent" : `Parent of ${testimonial.child}`}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Referral Program CTA */}
        <div className="bg-pink-500 rounded-3xl p-8 md:p-12 text-center text-white">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Earn {childcareData.referral.amount} for Every Referral
          </h3>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            {childcareData.referral.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleReferralEmail}
              className="bg-white text-pink-500 hover:bg-gray-50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Email a Friend
            </button>
            <button 
              onClick={handleShowDetails}
              className="border-2 border-white text-white hover:bg-white hover:text-pink-500 px-8 py-4 rounded-full font-semibold transition-all flex items-center justify-center"
            >
              <Info className="mr-2 h-5 w-5" />
              View Full Details
            </button>
          </div>
          
          <div className="mt-6 text-sm opacity-80">
            <p>Questions about referrals? Email us at {childcareData.referral.email}</p>
          </div>
        </div>
      </div>

      {/* Referral Details Modal */}
      <Modal 
        isOpen={showReferralDetails}
        onClose={() => setShowReferralDetails(false)}
        title="Referral Programme Details"
      >
        <div className="space-y-6">
          <div className="bg-pink-50 p-6 rounded-2xl border border-pink-200">
            <h3 className="text-2xl font-bold text-pink-600 mb-2">
              Earn {childcareData.referral.amount} Per Referral!
            </h3>
            <p className="text-slate-700">
              Help us grow our family and earn money for each successful referral.
            </p>
          </div>

          <div>
            <h4 className="text-xl font-semibold text-slate-800 mb-4 flex items-center">
              <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
              How It Works
            </h4>
            <div className="space-y-3">
              {childcareData.referral.detailedTerms.howItWorks.map((step, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                    {index + 1}
                  </div>
                  <p className="text-slate-700">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold text-slate-800 mb-4 flex items-center">
              <Info className="h-6 w-6 text-blue-500 mr-2" />
              Terms & Conditions
            </h4>
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
              <ul className="space-y-2">
                {childcareData.referral.detailedTerms.conditions.map((condition, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-slate-700 text-sm">{condition}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-xl border border-green-200">
            <p className="text-sm text-slate-700">
              <strong>Need Help?</strong> {childcareData.referral.detailedTerms.contactForDetails}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button 
              onClick={handleReferralEmail}
              className="flex-1 bg-pink-500 hover:bg-pink-600 text-white py-3 px-6 rounded-full font-semibold transition-all flex items-center justify-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Email a Friend Now
            </button>
            <button 
              onClick={handleReferralContact}
              className="flex-1 border-2 border-pink-500 text-pink-500 hover:bg-pink-500 hover:text-white py-3 px-6 rounded-full font-semibold transition-all flex items-center justify-center"
            >
              <ExternalLink className="mr-2 h-5 w-5" />
              Ask Questions
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default Testimonials;